import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Heart, ExternalLink, Newspaper } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface NewsPost {
  id: string;
  title: string;
  body: string;
  image_url: string | null;
  link_url: string | null;
  likes_count: number;
  published_at: string;
  profiles: { full_name: string | null; avatar_url: string | null } | null;
}

export default function NewsFeed() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["news-posts", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_posts" as any)
        .select("*, profiles:author_id(full_name, avatar_url)")
        .eq("organization_id", organization!.id)
        .order("published_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as NewsPost[];
    },
    enabled: !!organization?.id,
  });

  const { data: myLikes = [] } = useQuery({
    queryKey: ["my-news-likes", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("news_likes" as any)
        .select("post_id")
        .eq("user_id", user!.id);
      return ((data ?? []) as Array<{ post_id: string }>).map((l) => l.post_id);
    },
    enabled: !!user?.id,
  });

  const likeMutation = useMutation({
    mutationFn: async (postId: string) => {
      const liked = myLikes.includes(postId);
      if (liked) {
        await supabase.from("news_likes" as any).delete().eq("post_id", postId).eq("user_id", user!.id);
      } else {
        await supabase.from("news_likes" as any).insert({ post_id: postId, user_id: user!.id });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["news-posts"] });
      qc.invalidateQueries({ queryKey: ["my-news-likes"] });
    },
  });

  if (isLoading) return <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-28" />)}</div>;
  if (posts.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="font-semibold flex items-center gap-2">
        <Newspaper className="h-4 w-4" /> Nyheter
      </h3>
      {posts.map((post) => (
        <Card key={post.id} className="overflow-hidden">
          {post.image_url && (
            <img src={post.image_url} alt="" className="w-full h-40 object-cover" />
          )}
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                  {(post.profiles?.full_name || "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-muted-foreground">
                {post.profiles?.full_name} · {format(new Date(post.published_at), "d MMM", { locale: sv })}
              </span>
            </div>
            <h4 className="font-semibold text-sm">{post.title}</h4>
            <p className="text-sm text-muted-foreground line-clamp-3">{post.body}</p>
            {post.link_url && (
              <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline">
                <ExternalLink className="h-3 w-3" /> Läs mer
              </a>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className={`gap-1 h-8 ${myLikes.includes(post.id) ? "text-red-500" : "text-muted-foreground"}`}
                onClick={() => likeMutation.mutate(post.id)}
              >
                <Heart className={`h-3.5 w-3.5 ${myLikes.includes(post.id) ? "fill-current" : ""}`} />
                {post.likes_count || 0}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
